import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughFacadeService,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class EmailTemplatesWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly i18n = inject(I18nService);
  private readonly walkthroughFacadeService = inject(WalkthroughFacadeService);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.CreateEmailTemplates;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-email-templates-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_1.description',
          ),
          imageUrl: walkthroughAssetsResolver('email_templates.svg'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-email-templates-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 12, offsetY: -6 },
      },
    },
    {
      id: 'walkthrough-step-email-templates-3',
      position: 3,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-email-templates-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_5.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_5.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -12, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-email-templates-5',
      position: 5,

      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_6.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_6.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.email_templates.step_6.cta_text',
          ),
          ctaIcon: 'open-in-new',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, [
          'settings',
          'emails',
        ]);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  private finishHandler(): void {
    this.walkthroughFacadeService.openHelpArticleLink('workWithEmailTemplates');
    this.navigationHelperService.navigateAndOpenDrawer(['settings', 'emails']);
  }
}
